// Import the functions you need from the SDKs you need
import { initializeApp } from "firebase/app";
import { getAuth, GoogleAuthProvider } from "firebase/auth"; // Autenticação
import {
  getFirestore,
  doc,
  getDoc,
  deleteDoc,
  setDoc,
  updateDoc,
  addDoc,
  collection,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
  connectFirestoreEmulator,
} from "firebase/firestore"; // Firestore
import {
  connectFunctionsEmulator,
  getFunctions,
  httpsCallable,
} from "firebase/functions"; // Firebase Functions
import {
  getStorage,
  ref,
  uploadBytes,
  getDownloadURL,
  connectStorageEmulator,
} from "firebase/storage"; // Firebase Storage
import config from "./config/environment";

// Your web app's Firebase configuration
const firebaseConfig = {
  apiKey: config.firebase.apiKey,
  authDomain: config.firebase.authDomain,
  projectId: config.firebase.projectId,
  storageBucket: config.firebase.storageBucket,
  messagingSenderId: config.firebase.messagingSenderId,
  appId: config.firebase.appId,
  databaseURL: config.firebase.databaseURL,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Connect to Functions emulator if in development
if (config.emulators.functions.enabled) {
  connectFunctionsEmulator(
    getFunctions(app),
    config.emulators.host,
    config.emulators.functions.port,
  );
}

if (config.emulators.storage.enabled) {
  connectStorageEmulator(
    getStorage(app),
    config.emulators.host,
    config.emulators.storage.port,
  );
}

if (config.emulators.firestore.enabled) {
  connectFirestoreEmulator(
    getFirestore(app),
    config.emulators.host,
    config.emulators.firestore.port,
  );
}

// Initialize and export services
export const auth = getAuth(app);
export const db = getFirestore(app);
export const googleProvider = new GoogleAuthProvider();
export const functions = getFunctions(app, "us-central1");
export const storage = getStorage(app);

// Cloud Functions References
const promoteToTemporaryParticipantFunction = httpsCallable(
  functions,
  "promoteToTemporaryParticipant",
);
const restoreOriginalRoleFunction = httpsCallable(
  functions,
  "restoreOriginalRole",
);

// Secure Storage Functions
export const generateSignedUrlFunction = httpsCallable(
  functions,
  "generateSignedUrl",
);
export const checkFileExistsFunction = httpsCallable(
  functions,
  "checkFileExists",
);
export const listUserFilesFunction = httpsCallable(functions, "listUserFiles");

const updateUserMeetingStatus = async (
  userId: string,
  isInMeeting: boolean,
  meetingId?: string,
): Promise<boolean> => {
  const userRef = doc(db, "users", userId);
  await setDoc(
    userRef,
    {
      isInMeeting: isInMeeting,
      currentMeetingId: isInMeeting ? (meetingId ?? null) : null,
      lastMeetingActivity: serverTimestamp(),
    },
    { merge: true },
  );

  return true;
};

/**
 * Adiciona ou atualiza o documento de um participante em uma sala no Firestore.
 * Esta função é chamada quando um usuário entra na sala.
 * @param roomId - ID da sala
 * @param user - Objeto contendo uid, name, role e o avatar do usuário
 * @param sessionRole - O papel do usuário nesta sessão específica ('paciente', 'participante', etc.)
 * @param selectedAvatarId - O ID do avatar recém-escolhido na tela de seleção
 * @param sessionIdentityKey - Chave estável de sessão para deduplicar reconexões
 */
export const addUserToRoom = async (
  roomId: string,
  user: { uid: string; name: string; role: string; avatar?: string },
  sessionRole: string,
  selectedAvatarId?: string | null, // <-- PARÂMETRO ADICIONADO
  sessionIdentityKey?: string | null,
) => {
  if (!roomId || !user?.uid) {
    console.warn("addUserToRoom: roomId ou user.uid inválidos");
    return;
  }
  const participantRef = doc(db, "rooms", roomId, "participants", user.uid);

  await updateUserMeetingStatus(user.uid, true, roomId);
  const roleInSession = getRoleInSession(sessionRole);

  try {
    const existingParticipantDoc = await getDoc(participantRef);
    const existingRoleInSession = existingParticipantDoc.data()?.roleInSession;
    const existingAvatarId = existingParticipantDoc.data()?.avatarId ?? null;

    // Blindagem de avatar: numa RECONEXÃO, se não houver avatar recém-selecionado
    // nem avatar de perfil, NÃO sobrescrevemos com null — preservamos o avatar que
    // já estava no doc. Sem isso, uma reentrada após queda (intenção expirada e
    // perfil sem avatar) zerava o avatar do representante no cenário.
    const representativeAvatarId =
      selectedAvatarId || user.avatar || existingAvatarId || null;

    // Evita regressão de representante para observador em reconexões.
    if (existingRoleInSession === "representative") {
      await setDoc(
        participantRef,
        {
          userId: user.uid,
          name: user.name,
          displayNameNormalized: user.name?.trim().toLowerCase() || null,
          roleInSession: roleInSession,
          avatarId:
            roleInSession === "representative"
              ? representativeAvatarId
              : null,
          role: user.role,
          sessionIdentityKey: sessionIdentityKey || null,
        },
        { merge: true },
      );
      return;
    }

    await setDoc(
      participantRef,
      {
        userId: user.uid,
        name: user.name,
        displayNameNormalized: user.name?.trim().toLowerCase() || null,
        roleInSession: roleInSession,
        // CORREÇÃO: Prioriza o avatar recém-selecionado, depois o avatar do perfil
        // e, por fim, o avatar já existente no doc (blindagem contra zerar em queda).
        avatarId:
          roleInSession === "representative"
            ? representativeAvatarId
            : null,
        role: user.role,
        sessionIdentityKey: sessionIdentityKey || null,
      },
      { merge: true },
    );
  } catch (error) {
    console.error(
      `❌ addUserToRoom: Erro ao adicionar usuário ${user.name} na sala ${roomId}:`,
      error,
    );
  }
};

export function getRoleInSession(sessionRole: string) {
  let roleInSession: string;

  if (sessionRole === "participant") {
    roleInSession = "observer";
  } else if (sessionRole === "paciente" || sessionRole === "terapeuta") {
    roleInSession = "representative";
  } else {
    roleInSession = "observer"; // Fallback seguro
  }
  return roleInSession;
}

/**
 * Remove o documento de um participante de uma sala no Firestore.
 */
export const removeUserFromRoom = async (roomId: string, userId: string) => {
  if (!roomId || !userId) return;

  const participantRef = doc(db, "rooms", roomId, "participants", userId);
  try {
    await deleteDoc(participantRef);
  } catch (error) {
    console.error("Erro ao remover usuário da sala:", error);
  }
};

const clampRelativePosition = (value: number, min = 0.05, max = 0.95) => {
  return Math.max(min, Math.min(max, value));
};

export const createVirtualAvatar = async (
  roomId: string,
  data: { name: string; avatarId: string; createdBy: string; createdByName?: string | null }
): Promise<string> => {
  if (!roomId) {
    throw new Error("Sala inválida para criação de avatar virtual.");
  }

  const virtualAvatarsRef = collection(db, "rooms", roomId, "virtualAvatars");
  const baseXRel = clampRelativePosition(0.15 + Math.random() * 0.7);
  const baseYRel = clampRelativePosition(0.2 + Math.random() * 0.5);

  const timestamp = serverTimestamp();
  const docRef = await addDoc(virtualAvatarsRef, {
    name: data.name.trim() || "Avatar",
    avatarId: data.avatarId,
    pose: "standing",
    direction: "front",
    xRel: baseXRel,
    yRel: baseYRel,
    createdBy: data.createdBy,
    createdByName: data.createdByName ?? null,
    createdAt: timestamp,
    updatedAt: timestamp,
    isVirtual: true,
  });

  return docRef.id;
};

export const updateVirtualAvatar = async (
  roomId: string,
  virtualAvatarId: string,
  updates: Record<string, unknown>
) => {
  if (!roomId || !virtualAvatarId) return;

  const avatarRef = doc(db, "rooms", roomId, "virtualAvatars", virtualAvatarId);
  await updateDoc(avatarRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  });
};

export const deleteVirtualAvatar = async (roomId: string, virtualAvatarId: string) => {
  if (!roomId || !virtualAvatarId) return;

  const avatarRef = doc(db, "rooms", roomId, "virtualAvatars", virtualAvatarId);
  await deleteDoc(avatarRef);
};

export const createSceneObject = async (
  roomId: string,
  data: { name?: string; objectId: string; createdBy: string; createdByName?: string | null }
): Promise<string> => {
  if (!roomId) throw new Error("Sala inválida para criação de objeto.");

  const objectsRef = collection(db, "rooms", roomId, "sceneObjects");
  const baseXRel = clampRelativePosition(0.15 + Math.random() * 0.7);
  const baseYRel = clampRelativePosition(0.2 + Math.random() * 0.5);

  const timestamp = serverTimestamp();
  const docRef = await addDoc(objectsRef, {
    name: data.name?.trim() || "",
    objectId: data.objectId,
    xRel: baseXRel,
    yRel: baseYRel,
    createdBy: data.createdBy,
    createdByName: data.createdByName ?? null,
    createdAt: timestamp,
    updatedAt: timestamp,
  });

  return docRef.id;
};

export const updateSceneObject = async (
  roomId: string,
  sceneObjectId: string,
  updates: Record<string, unknown>
) => {
  if (!roomId || !sceneObjectId) return;
  const objectRef = doc(db, "rooms", roomId, "sceneObjects", sceneObjectId);
  await updateDoc(objectRef, { ...updates, updatedAt: serverTimestamp() });
};

export const deleteSceneObject = async (roomId: string, sceneObjectId: string) => {
  if (!roomId || !sceneObjectId) return;
  const objectRef = doc(db, "rooms", roomId, "sceneObjects", sceneObjectId);
  await deleteDoc(objectRef);
};

/**
 * Atualiza o avatar de um participante específico dentro de uma sala.
 */
export const updateParticipantAvatar = async (
  roomId: string,
  userId: string,
  avatarId: string,
) => {
  if (!roomId || !userId || !avatarId) return;
  const participantRef = doc(db, "rooms", roomId, "participants", userId);
  try {
    await updateDoc(participantRef, {
      avatarId: avatarId,
    });
  } catch (error) {
    console.error("Erro ao atualizar o avatar do participante:", error);
    throw error;
  }
};

/**
 * Promove usuário para participante temporário usando Cloud Function.
 */
export const promoteToParticipant = async (appointmentId: string) => {
  if (!appointmentId) return;
  try {
    const result = await promoteToTemporaryParticipantFunction({
      appointmentId,
    });
    if ((result.data as any)?.success) {
      return result.data;
    } else {
      throw new Error("Falha na promoção do usuário");
    }
  } catch (error) {
    console.error("Erro ao promover usuário:", error);
    throw error;
  }
};

/**
 * Restaura o role original do usuário usando Cloud Function.
 */
export const restoreOriginalRole = async (appointmentId?: string) => {
  try {
    const result = await restoreOriginalRoleFunction({ appointmentId });
    if ((result.data as any)?.success) {
      return result.data;
    } else {
      throw new Error("Falha na restauração do role");
    }
  } catch (error) {
    console.error("Erro ao restaurar role original:", error);
    throw error;
  }
};

/**
 * Faz upload de arquivo simples para o Firebase Storage com validações de segurança
 * @param file - Arquivo a ser enviado
 * @param path - Caminho no Storage onde o arquivo será salvo
 * @returns Promise com a URL pública do arquivo
 */
export const uploadFileSimple = async (
  file: File,
  path: string,
): Promise<string> => {
  // Validações de segurança
  const MAX_SIZE = 10 * 1024 * 1024; // 10MB
  const ALLOWED_TYPES = [
    "image/jpeg",
    "image/png",
    "image/jpg",
    "application/pdf",
  ];

  if (file.size > MAX_SIZE) {
    throw new Error("Arquivo muito grande. Tamanho máximo: 10MB");
  }

  if (!ALLOWED_TYPES.includes(file.type)) {
    throw new Error("Tipo de arquivo não permitido. Use: JPEG, PNG ou PDF");
  }

  try {
    const storageRef = ref(storage, path);
    const snapshot = await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(snapshot.ref);
    return downloadURL;
  } catch (error) {
    console.error("Erro no upload do arquivo:", error);
    throw error;
  }
};

/**
 * Faz upload da foto de perfil do terapeuta com validações específicas
 * @param userId - ID do usuário
 * @param file - Arquivo da foto
 * @returns Promise com a URL pública da foto
 */
export const uploadProfilePhoto = async (
  userId: string,
  file: File,
): Promise<string> => {
  // Validação específica para fotos de perfil
  const PHOTO_TYPES = ["image/jpeg", "image/png", "image/jpg"];
  if (!PHOTO_TYPES.includes(file.type)) {
    throw new Error("Foto de perfil deve ser JPEG ou PNG");
  }

  const fileExtension = file.name.split(".").pop();
  const fileName = `profile-${Date.now()}.${fileExtension}`;
  const path = `therapists/${userId}/profile/${fileName}`;

  return uploadFileSimple(file, path);
};

/**
 * Faz upload do certificado/diploma com validações específicas
 * @param userId - ID do usuário
 * @param certificateId - ID do certificado
 * @param file - Arquivo do certificado
 * @returns Promise com a URL pública do certificado
 */
export const uploadCertificate = async (
  userId: string,
  certificateId: string,
  file: File,
): Promise<string> => {
  // Validação específica para certificados (PDF ou imagens)
  const CERT_TYPES = [
    "application/pdf",
    "image/jpeg",
    "image/png",
    "image/jpg",
  ];
  if (!CERT_TYPES.includes(file.type)) {
    throw new Error("Certificado deve ser PDF, JPEG ou PNG");
  }

  const fileExtension = file.name.split(".").pop();
  const fileName = `certificate-${certificateId}-${Date.now()}.${fileExtension}`;
  const path = `therapists/${userId}/certificates/${fileName}`;

  return uploadFileSimple(file, path);
};

/**
 * Cria ou atualiza a privacidade de uma sala.
 * @param roomId - ID da sala
 * @param isPublic - Novo status de privacidade
 * @param userId - ID do terapeuta (necessário para createdBy se o documento não existir)
 */
export const updateRoomPrivacy = async (
  roomId: string,
  isPublic: boolean,
  userId: string,
) => {
  if (!roomId || !userId) {
    console.warn("updateRoomPrivacy: roomId ou userId inválidos");
    return;
  }

  const roomRef = doc(db, "rooms", roomId);

  try {
    // Verifica se o documento existe para inicializar invitedUsers
    const roomDoc = await getDoc(roomRef);
    const updateData: any = {
      isPublic: isPublic,
      createdBy: userId,
      lastUpdated: new Date(),
    };

    // Se o documento não existe, inicializa invitedUsers como array vazio
    if (!roomDoc.exists()) {
      updateData.invitedUsers = [];
    }

    // Usa setDoc com merge - funciona tanto na criação quanto na atualização
    await setDoc(roomRef, updateData, { merge: true });
  } catch (error) {
    throw error;
  }
};

/**
 * Adiciona um usuário à lista de usuários convidados de uma sala.
 * @param roomId - ID da sala
 * @param userId - ID do usuário a ser adicionado
 */
export const addUserToInvitedUsers = async (roomId: string, userId: string) => {
  if (!roomId || !userId) {
    console.warn("addUserToInvitedUsers: roomId ou userId inválidos");
    return;
  }

  const roomRef = doc(db, "rooms", roomId);

  try {
    await updateDoc(roomRef, {
      invitedUsers: arrayUnion(userId),
    });

    console.log(
      `Usuário ${userId} adicionado à lista de convidados da sala ${roomId}`,
    );
  } catch (error) {
    console.error("Erro ao adicionar usuário à lista de convidados:", error);
    throw error;
  }
};

/**
 * Ativa uma sala (isActive: true) e registra o início da sessão.
 * NOTA: Esta função agora apenas marca a sala como ativa.
 * A criação do meeting do Dyte é feita automaticamente pelo getDyteMeeting.
 * @param roomId - ID da sala
 * @param userId - ID do terapeuta que está ativando a sala
 */
export const activateRoom = async (roomId: string, userId: string) => {
  if (!roomId || !userId) {
    console.warn("activateRoom: roomId ou userId inválidos");
    return;
  }
  await updateUserMeetingStatus(userId, true, roomId);

  const roomRef = doc(db, "rooms", roomId);

  try {
    await setDoc(
      roomRef,
      {
        isActive: true,
        createdBy: userId,
        lastActivity: serverTimestamp(),
        sessionStartedAt: serverTimestamp(),
      },
      { merge: true },
    );

    console.log(`✅ Sala ${roomId} ativada pelo terapeuta ${userId}`);
  } catch (error) {
    console.error("❌ Erro ao ativar sala:", error);
    throw error;
  }
};

/**
 * Remove um usuário da lista de usuários convidados de uma sala.
 * @param roomId - ID da sala
 * @param userId - ID do usuário a ser removido
 */
export const removeUserFromInvitedUsers = async (
  roomId: string,
  userId: string,
) => {
  if (!roomId || !userId) {
    console.warn("removeUserFromInvitedUsers: roomId ou userId inválidos");
    return;
  }

  const roomRef = doc(db, "rooms", roomId);

  try {
    await updateDoc(roomRef, {
      invitedUsers: arrayRemove(userId),
    });

    console.log(
      `Usuário ${userId} removido da lista de convidados da sala ${roomId}`,
    );
  } catch (error) {
    console.error("Erro ao remover usuário da lista de convidados:", error);
    throw error;
  }
};

export const getMeetingIdFromRoom = async (roomId: string) => {
  const roomRef = doc(db, "dyteMeetings", roomId);
  const roomSnap = await getDoc(roomRef);

  if (!roomSnap.exists()) {
    return false;
  }
  return roomSnap.data()?.meetingId;
};

export const getRoomOwnerId = (roomId: string): string => {
  const parts = roomId.split("-");
  return parts[1];
}

export { app };
